import React, { useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { useUntypedTranslation } from '../../../hooks/useUntypedTranslation';
import { pageSurface } from '../../../styles/visualLanguage';
import { AdvisoryLeaveButton } from './AdvisoryLeaveButton';

/**
 * AdvisoryThinkingState
 *
 * The single full-screen thinking treatment for every supervisor turn in
 * flight (ask, refine, confirm) — reused as-is, not forked, for each. An
 * earlier plan expected a second caller for an "engagement-match turn"
 * narrated by a since-deleted `AdvisoryMatchGate` component; that never
 * materialized because classification completes synchronously inside the
 * confirm turn's own SSE stream, so the confirm turn's own in-flight
 * rendering of this same component already covers it — no titleKey exists
 * for it. It is a full-page treatment, deliberately NOT the wizard shell
 * (`AdvisoryEngagementShell`): a thinking state is not a counted step, and
 * the prototype's `.run` scene (flow-chrome.css:280-295) carries no
 * `.wiz-top` chrome at all — no step counter and no action row.
 *
 * It does now carry ONE piece of that chrome: the leave affordance
 * (`AdvisoryLeaveButton`, shared with the wizard shell and the run panel).
 * The prototype's own lack of one is not an argument for leaving this screen
 * without an exit — an analysis wait is twenty minutes and this is what
 * stands there until the first turn reports, so "no leave affordance" meant
 * the browser's Back button was the only way out of the longest wait in the
 * product. The step counter stays absent: that reasoning was about a COUNTED
 * step, and this still is not one.
 *
 * The affordance renders unconditionally, `suppressLeaveNote` included.
 * Suppression is about the note's promise, never about whether leaving is
 * allowed — even for the evidence hand-off, where THIS screen does not
 * survive a reload, leaving is non-destructive: the turn is already posted,
 * `useSupervisorTurn` does not abort on unmount, and re-entry derives
 * `analysisRunning` from the document and lands on the run panel. What is
 * not kept is this screen; the engagement is.
 *
 * It sits OUTSIDE the polite live region for the same reason it does on the
 * run panel: chrome inside one is re-announced on every change.
 *
 * The copy is static: a title plus an unconditional "you can leave this
 * page and come back" note, present whether or not a stream is running.
 * `progressPhase` is layered on only when this tab owns the live turn
 * (i.e. `useSupervisorTurn`'s own `progressPhase`, passed through by the
 * caller) — on reload there is no stream, so the component falls back to
 * the static line alone and stays honest. The prototype's cycling
 * `.run-line` narration (flow-chrome.css:291-292, flow-data.en.js:224-242)
 * is deliberately NOT reproduced here: those lines describe per-engagement
 * work no one is doing, and fabricating a similar cycle here would be
 * narration for nothing.
 *
 * `progressPhase` is provider-derived text (`useSupervisorTurn`'s mapped
 * `status` SSE payload) and is rendered as a JSX text node only, via
 * ordinary interpolation — never as raw markup. No Advisory surface may
 * ever inject a provider string as HTML.
 *
 * `suppressLeaveNote` hides the leave-and-return note only. The evidence
 * hand-off's wait is not resumable (component state only) — a reload lands
 * back on the evidence screen, not on this hand-off — so the note's "your
 * place is kept" promise would be false there. Both existing call sites
 * omit this prop, so it defaults to `undefined` and their rendered output
 * is unchanged.
 *
 * This component is always mounted as a full REPLACEMENT — a sibling early
 * return in `AdvisoryEvidenceScreen.tsx` and `AdvisoryEngagementPage.tsx`
 * (the former `AdvisoryMatchGate.tsx` call site is deleted), never a nested
 * child alongside other content. That is what makes a single, polite
 * (`aria-live="polite"`) live region correct on the outer element: there is
 * never a competing announcement from sibling content to interrupt, so an
 * assertive region would gain nothing while being needlessly disruptive.
 *
 * The same "always a full replacement" fact is why a plain mount-only
 * `useEffect(() => headingRef.current?.focus(), [])` is correct here
 * rather than something reacting to a prop change — "on mount" and "on
 * transition" are the same event for this component, since it never
 * updates in place. Moving focus to the heading on every mount is also
 * never a blind steal: whatever this component replaces (a form, a
 * button) is gone from the DOM the instant this mounts, so focus was
 * already about to fall back to `document.body` with nothing announced —
 * that exact defect was observed live for the evidence hand-off. Landing
 * it on the new `h1` instead is strictly an improvement over that default.
 */
export interface AdvisoryThinkingStateProps {
  titleKey: string;
  progressPhase?: string | null;
  suppressLeaveNote?: boolean;
}

export function AdvisoryThinkingState({
  titleKey,
  progressPhase,
  suppressLeaveNote,
}: AdvisoryThinkingStateProps) {
  const { t } = useUntypedTranslation();
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    // Runs once per mount — this component is always a REPLACEMENT (a
    // sibling early return in every caller, never nested), so "on mount"
    // and "on transition" coincide. See the docblock above for why this is
    // never a blind focus steal.
    headingRef.current?.focus();
  }, []);

  return (
    <Box sx={[pageSurface, { display: 'flex', flexDirection: 'column', p: 2.5 }]}>
      {/* Chrome, not status: outside the live region below, so it is not re-announced
          every time `progressPhase` changes. */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <AdvisoryLeaveButton />
      </Box>

      <Box
        role="status"
        aria-live="polite"
        sx={{ flex: 1, display: 'grid', placeItems: 'center', textAlign: 'center' }}
      >
        <Box
          sx={theme => ({
            display: 'grid',
            justifyItems: 'center',
            maxWidth: theme.visualPrimitives.runInnerMaxWidth,
            gap: theme.visualPrimitives.runInnerGap,
          })}
        >
          <Typography
            component="h1"
            ref={headingRef}
            tabIndex={-1}
            sx={theme => ({
              fontSize: theme.visualPrimitives.runTitleSize,
              fontWeight: theme.visualPrimitives.runTitleWeight,
              letterSpacing: theme.visualPrimitives.runTitleTracking,
              lineHeight: theme.visualPrimitives.runTitleLineHeight,
              color: theme.colors.inkPrimary,
              margin: 0,
            })}
          >
            {t(titleKey)}
          </Typography>
          {suppressLeaveNote ? null : (
            <Typography
              data-testid="advisory-thinking-leave-note"
              sx={theme => ({
                fontSize: theme.visualPrimitives.runNoteSize,
                lineHeight: theme.visualPrimitives.runNoteLineHeight,
                color: theme.colors.inkTertiary,
              })}
            >
              {t('advisory:thinking.leaveAndReturn')}
            </Typography>
          )}
          {progressPhase ? (
            <Typography
              data-testid="advisory-progress-phase"
              sx={theme => ({
                fontSize: theme.visualPrimitives.runLineSize,
                lineHeight: theme.visualPrimitives.runLineLineHeight,
                color: theme.colors.inkSecondary,
              })}
            >
              {progressPhase}
            </Typography>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
}

export default AdvisoryThinkingState;
